import React, { useCallback, useEffect, useMemo, useState } from "react";
import { UnitFormatsProvider } from "@strava/units/react";

import { UNIT_METRIC } from '@strava/units';
import {
  Map,
  getElevationFromStream,
  getPrivacyZonesFromPrivacyStream,
  getHighlightSectionFromIndices,
  type PolylineHighlightSection,
  type ActivitySection,
   } from "@strava/feature-global-wasm-mre";

import I18n from '../../utils/I18n';
import StravaPackageContextProviders from "../../apps/StravaPackageContextProviders";

import { MapPhotos } from "./MapPhotos/MapPhotos";
import { MapCustomControls } from "./MapCustomControls/MapCustomControls";
import { MapResizeHandler } from "./MapResizeHandler/MapResizeHandler";
import { MapScrubber } from "./MapScrubber/MapScrubber";

import { getPhotosWithAttributes } from "./helpers/getPhotosWithAttributes";
import { getSplitMarkerLocationsFromIndices } from "./helpers/getSplitMarkerLocationsFromIndices";
import type { ActivityDetailsMapProps, LatLng, Photo } from "./types";

import styles from "./ActivityDetailsMap.scss";

const ActivityDetailsMap = ({
  activityId,
  analyticsCategory,
  streamData,
  defaultHighlight,
  showCreateRoute = true,
  showGpxDownload = true,
  showFullScreenToggle = false,
  showFlyover = true,
  measurementUnits = UNIT_METRIC,
  mapCreatedCallback,
  staticMapUrl = null, // This should not be set for views that require map interactivity e.g. privacy zones
  hasPrivacyZonesAdmin = false,
  privacyZones = [],
  isSubscriber = false,
  grabberElement = null
}: ActivityDetailsMapProps) => {

  const {
    latlng: locationStream = [],
    privacy: privacyStream = [],
    altitude: elevationStream = [],
    distance: distanceStream = []
  } = streamData || {};

  // Get the elevation data from the elevation and distance streams
  const elevation = useMemo(
    () =>
      elevationStream && elevationStream.length > 0 &&
      distanceStream && distanceStream?.length > 0
        ? getElevationFromStream(elevationStream, distanceStream)
        : [],
    [elevationStream, distanceStream]
  );

  // For some views, we want to highlight a section of the route by default, i.e. when viewing a map
  // for a segment within an activity
  const defaultHighlightLatLng: LatLng[] | null = defaultHighlight
    ? [...locationStream.slice(defaultHighlight[0], defaultHighlight[1])]
    : null;

  const privacyZonesActivitySections: ActivitySection[] = useMemo(() => 
    getPrivacyZonesFromPrivacyStream(privacyStream), [privacyStream]);

  const [currentMapBounds, setCurrentMapBounds] = useState<LatLng[]>(
    defaultHighlightLatLng ?? locationStream
  );

  const [highlightSection, setHighlightSection] = useState<PolylineHighlightSection | null>(
    defaultHighlight?.length === 2 ? // if valid `defaultHighlight` provided
    getHighlightSectionFromIndices(defaultHighlight[0], defaultHighlight[1]) : // initialize highlight section
    null // else null
  );

  const [mapPhotos, setMapPhotos] = useState<Photo[] | null>(null);
  const [splitMarkers, setSplitMarkers] = useState<LatLng[] | null>(null); // For Activity Split view

  const [showPrivacyZones, setShowPrivacyZones] = useState<boolean>(false);

  const [showPaywallModal, setShowPaywallModal] = useState<boolean>(false);


  const showPrivacyZonesControl: boolean = useMemo(() => {
    return hasPrivacyZonesAdmin && privacyZones && privacyZones.length > 0;
  }, [hasPrivacyZonesAdmin, privacyZones]);

  const onHighlightPolyline = useCallback((e) => {
    const { start, end } = e.detail;

    if (typeof start !== 'number' || typeof end !== 'number') {
      return;
    }

    setHighlightSection(getHighlightSectionFromIndices(start, end)); // Set the highlight section based on indices
  }, []);

  const onUnhighlightPolyline = useCallback(() => {
    setHighlightSection(null); // Clear the highlight section
  }, []);

  const onHighlightPolylineAndCenterOnMap = useCallback((e) => {
    const { start, end } = e.detail;

    if (typeof start !== 'number' || typeof end !== 'number') {
      return;
    }

    const latLng = [...locationStream.slice(start, end)];

    setHighlightSection(getHighlightSectionFromIndices(start, end)); // Set the highlight section based on indices
    setCurrentMapBounds(latLng); // Center map on the highlighted section
  }, [locationStream]);

  const onResetMapBounds = useCallback(() => {
    setCurrentMapBounds(locationStream);
  }, [locationStream]);

  // Reset all map state to defaults. Dispatched as a single event on subview transitions
  // to clear highlights, splits, bounds, etc. from the previous view.
  const onResetMap = useCallback(() => {
    // Spread to create a new array reference — if currentMapBounds is already locationStream,
    // React would skip the update. A new reference ensures the polyline prop changes in Map.tsx,
    // triggering flyToBounds to reset the camera position and zoom level.
    setCurrentMapBounds([...locationStream]);
    setHighlightSection(null);
    setSplitMarkers(null);
  }, [locationStream]);


  const onRenderMapImages = useCallback((e) => {
    const { photos = [] } = e.detail;

    // Get photos with required attributes (i.e. lat, lng, thumbnail) and set state
    setMapPhotos(getPhotosWithAttributes(photos));
  }, []);

  // Handler for updating split indices (Activity Split view)
  const onUpdateSplitIndices = useCallback((e) => {
    const { indices = [] } = e.detail;

    // Get split marker locations from indices and set state
    setSplitMarkers(getSplitMarkerLocationsFromIndices(indices, locationStream));
  }, [locationStream]);

  const eventListeners = useMemo(() => ({
    HighlightPolyline: onHighlightPolyline,
    UnhighlightPolyline: onUnhighlightPolyline,
    HighlightPolyLineAndCenterOnMap: onHighlightPolylineAndCenterOnMap,
    ResetMapBounds: onResetMapBounds,
    ResetMap: onResetMap,
    RenderMapImages: onRenderMapImages,
    UpdateSplitIndices: onUpdateSplitIndices
  }), [
    onHighlightPolyline,
    onUnhighlightPolyline,
    onHighlightPolylineAndCenterOnMap,
    onResetMapBounds,
    onResetMap,
    onRenderMapImages,
    onUpdateSplitIndices
  ]);

  // Attach and detach event listeners
  useEffect(() => {
    Object.entries(eventListeners).forEach(([event, listener]) => {
      document.addEventListener(event, listener);
    });

    /** 
     * Call the mapCreatedCallback if provided, AFTER event listeners are attached
     *
     * Currently, this is used to trigger a call to `renderPhotos()` in _photos.haml from the various activity type HAML partials
     * e.g. https://github.com/strava/active/blob/main/app/views/labs/activities/run.haml#L89-L91
     * 
     * `renderPhotos()` in turn dispatches a `RenderMapImages` event (https://github.com/strava/active/blob/main/app/views/labs/activities/_photos.haml#L24-L25)
     * This component i.e. ActivityDetailsMap listens for that event and updates state with the photos to be rendered on the map
     */
    if (mapCreatedCallback) {
      mapCreatedCallback()();
    }

    return () => {
      Object.entries(eventListeners).forEach(([event, listener]) => {
        document.removeEventListener(event, listener);
      });
    };
  }, [eventListeners, mapCreatedCallback]);

  const handlePreviewEnd = useCallback(() => {
    setShowPaywallModal(true);
  }, []);

  return (
    <StravaPackageContextProviders> 
      <UnitFormatsProvider
        locale={I18n.language()}
        unitSystem={measurementUnits}
      >
        <Map
          showPOIs={true}
          page={process.env.ACTIVITY_DETAILS_ANALYTICS_PAGE || ''}
          category={analyticsCategory || ''}
          surfaceIdentifier={process.env.ACTIVITY_DETAILS_SURFACE_IDENTIFIER}
          polyline={currentMapBounds || locationStream}
          notSupportedFallbackElement={staticMapUrl
            ? <img src={staticMapUrl} alt={I18n.t('mre.static_map')} className={styles.staticMapFallback} /> // Fallback to static map image if URL provided
            : null} // Fallback to static map image if URL provided
        >
          <Map.UIControls
            flyover={showFlyover}
            topRightOverride={
              <div className={styles.topRightOverride}>
                <MapCustomControls
                  {...(showCreateRoute && { createRouteURL: `/maps/create?activityId=${activityId}` })}
                  {...(showGpxDownload && { gpxDownloadURL: `/activities/${activityId}/export_gpx` })}
                  showMapTypeControl={true}
                  showFullScreenToggle={showFullScreenToggle}
                  {...(showPrivacyZonesControl && { showPrivacyZonesControl, showPrivacyZones, setShowPrivacyZones })}
                />

                {/* If there are any privacy zones to display, show the legend */}
                {privacyZonesActivitySections.length > 0 && (
                  <Map.PrivacyZonesLegend className={styles.privacyZonesLegend} />
                )}

              </div>
            }
          />

          <Map.Activity
            id={activityId.toString()}
            polyline={locationStream}
            activitySections={privacyZonesActivitySections}
            highlights={highlightSection ? [highlightSection] : undefined} // Pass `highlightSection` inside array if it exists
          />

          <MapResizeHandler grabberElement={grabberElement} />
          <MapScrubber locationStream={locationStream} />

          {/* Render split markers if any (Activity Split view) */}
          {splitMarkers?.map((latLng) => (
            <Map.Marker
              key={`split-marker-${latLng[0]}-${latLng[1]}`}
              latLng={latLng}
              type='route-scrub'
            />
          ))}

          {showFlyover && elevation && elevation.length > 0 && (
            <Map.Flyover
              polyline={locationStream}
              elevation={elevation}
              isPreview={!isSubscriber} // Non-subscribers see preview mode
              onPreviewEnd={handlePreviewEnd} // Callback when preview ends (6 secs)
            />
          )}

          {!isSubscriber && showFlyover && (
            <Map.PaywallModal
              showPaywallModal={showPaywallModal}
              setShowPaywallModal={setShowPaywallModal}   
            />
          )}

          {mapPhotos && <MapPhotos mapPhotos={mapPhotos} />}

          {showPrivacyZones && <Map.PrivacyZones privacyZones={privacyZones} />}
        </Map>
      </UnitFormatsProvider>
    </StravaPackageContextProviders>
  );
};

export default ActivityDetailsMap;
